<template>
  <div>
    <div
      v-if="displayedContacts.length > 0"
      ref="contactListEl"
      class="contacts-list"
    >
      <ContactItem
        v-for="contact in displayedContacts"
        :key="contact.id"
        :contact="contact"
        class="contact-item"
      />
      <div
        v-if="showLoadMore"
        ref="infiniteScrollTrigger"
        class="infinite-scroll-trigger"
      >
        <div v-if="isLoadingMore" class="loading-more">
          <Spinner />
          <span>Loading...</span>
        </div>
        <div v-else class="load-trigger">
          <BaseButton
            kind="auxiliary"
            text="Load More Contacts"
            class="load-more-button"
            @click="loadMore"
          />
        </div>
      </div>
    </div>
    <div v-else class="empty-state">
      <p>No contacts found</p>
    </div>
  </div>
</template>

<script lang="ts">
import { debounce } from "lodash-es";
import { computed, defineComponent, onMounted, ref, toRefs, watch } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import ContactItem from "@/components/contacts/ContactItem.vue";
import {
  asArray,
  asGraphRecordResult,
  asString,
} from "@/components/graph/result";
import * as inject from "@/components/inject";
import Spinner from "@/components/Spinner.vue";
import { useReleaser } from "@/ref";
import { mapIfDefined } from "@/utils/undefined";

// Pagination configuration
const ITEMS_PER_PAGE = 50;

export interface Contact {
  id: string;
  full_name: string;
  given_name: string;
  family_name: string;
  email: string;
  avatar?: string;
  hasNotes: boolean;
}

export default defineComponent({
  components: {
    BaseButton,
    ContactItem,
    Spinner,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  emits: {
    "update:total": (_count: number) => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();
    const profile = inject.profile();
    const { searchQuery } = toRefs(props);

    const currentPage = ref(1);
    const isLoadingMore = ref(false);
    const infiniteScrollTrigger = ref<HTMLElement | null>(null);
    const contactListEl = ref<HTMLElement | null>(null);

    const graphQueryString = computed(() => {
      const query = searchQuery.value.trim();
      if (query) {
        return `people where FullName ~ "${query}" or Email ~ "${query}"`;
      }
      return "people";
    });

    const allContacts = ref<Contact[]>([]);

    const loadContacts = async (queryStr: string) => {
      try {
        // Contacts live in the personal workspace.
        const workspaceId = profile.value.user.personal.workspaceId;

        const compiledQuery = await controller.graph.query.compile(
          workspaceId,
          queryStr,
          {}
        );

        const queryValue = compiledQuery.value;
        compiledQuery.release();

        if (!queryValue || "error" in queryValue) {
          window.console.warn("Unable to compile the query:", queryValue);
          allContacts.value = [];
          return;
        }

        const resultRef = await controller.graph.query.run(queryValue.value);
        const result = resultRef.value;
        resultRef.release();

        if (!result || "error" in result) {
          window.console.error("Unable to run the query:", result);
          allContacts.value = [];
          return;
        }

        const contacts: Contact[] = [];
        if (result.value instanceof Map) {
          for (const [_, rawPeopleData] of result.value.entries()) {
            const peopleData = asGraphRecordResult(rawPeopleData);
            if (peopleData) {
              const id = mapIfDefined(asString)(
                mapIfDefined(asGraphRecordResult)(peopleData._routine)?.id
              )!;

              const hasNotes =
                mapIfDefined<number, boolean>((n) => n > 0)(
                  mapIfDefined(asArray)(
                    mapIfDefined(asGraphRecordResult)(peopleData.Notes)?.blocks
                  )?.length
                ) ?? false;

              const contact: Contact = {
                id,
                full_name:
                  asString(peopleData.FullName) ||
                  asString(peopleData.Email) ||
                  "Unknown",
                given_name: asString(peopleData.GivenName) || "",
                family_name: asString(peopleData.FamilyName) || "",
                email: asString(peopleData.Email) || "",
                avatar: asString(peopleData.Avatar),
                hasNotes,
              };
              contacts.push(contact);
            }
          }
        }

        // Sort contacts with notes first, then by name
        contacts.sort((a, b) => {
          if (a.hasNotes !== b.hasNotes) {
            return a.hasNotes ? -1 : 1;
          }
          const nameA = a.full_name || "";
          const nameB = b.full_name || "";
          return nameA.localeCompare(nameB);
        });

        allContacts.value = contacts;
        currentPage.value = 1;
      } catch (error) {
        window.console.error("Error loading contacts:", error);
        allContacts.value = [];
      }
    };

    const releaser = useReleaser();

    onMounted(() => {
      const loadMoreDebounced = debounce(async () => {
        await loadMore();
      }, 200);

      const handleScroll = debounce(() => {
        const triggerElement = infiniteScrollTrigger.value;
        if (!triggerElement || isLoadingMore.value) {
          return;
        }

        const rect = triggerElement.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const isVisible = rect.top < windowHeight + 200; // 200px buffer

        if (isVisible) {
          loadMoreDebounced();
        }
      }, 100);

      const scrollContainers = [
        window,
        document,
        document.documentElement,
        document.body,
      ];

      scrollContainers.forEach((container) => {
        container.addEventListener("scroll", handleScroll, {
          passive: true,
        });
      });

      releaser.defer(() => {
        scrollContainers.forEach((container) => {
          if (container && container.removeEventListener) {
            container.removeEventListener("scroll", handleScroll);
          }
        });
        loadMoreDebounced.cancel();
        handleScroll.cancel();
      });
    });

    await loadContacts(graphQueryString.value);

    const filteredContacts = computed(() => {
      return allContacts.value;
    });

    const displayedContacts = computed(() => {
      const endIndex = currentPage.value * ITEMS_PER_PAGE;
      return filteredContacts.value.slice(0, endIndex);
    });

    const showLoadMore = computed(() => {
      return displayedContacts.value.length < filteredContacts.value.length;
    });

    releaser.defer(
      watch(
        filteredContacts,
        (contacts) => {
          emit("update:total", contacts.length);
        },
        { immediate: true }
      )
    );

    const loadMore = async () => {
      if (isLoadingMore.value) return;

      isLoadingMore.value = true;
      // Simulate loading delay for better UX
      await new Promise((resolve) => setTimeout(resolve, 300));
      currentPage.value++;
      isLoadingMore.value = false;
    };

    return {
      contactListEl,
      currentPage,
      displayedContacts,
      filteredContacts,
      infiniteScrollTrigger,
      isLoadingMore,
      loadMore,
      showLoadMore,
    };
  },
});
</script>

<style scoped lang="scss">
.contacts-list {
  display: flex;
  flex-direction: column;
  // 64px page inset minus the row's 16px padding, so names stay aligned
  // with the header while the hover extends past the avatar.
  padding-inline: 48px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  @extend %font-body-regular;
  @include themed() {
    color: t($grey-40);
  }
}

.infinite-scroll-trigger {
  display: flex;
  justify-content: center;
  padding: 24px;
  min-height: 80px;

  .loading-more {
    display: flex;
    align-items: center;
    gap: 12px;
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-50);
    }
  }

  .load-trigger {
    display: flex;
    justify-content: center;
  }
}
</style>
